import type { EventSink } from '../events'
import { JUMP, PET, pressed } from '../inputBits'
import type { InputBits } from '../inputBits'
import { NO_TICK } from '../world'
import type { PlayerState, World } from '../world'
import { activeRide, canSummon, isBurrowed, summon, updateRide } from './rides'

/**
 * Step 1: turn this tick's input into intent (jump buffer and release, jump). Returns the
 * effective bits for step 2: 0 while stunned or finished. Respawning players are frozen; only
 * `prevBits` follows the raw input. PET summons a ride when the meter is full; while riding a
 * Drillmole the first grounded JUMP press burrows instead of jumping (D-050). The retired SLIDE,
 * SPRINT and SLOW bits are ignored
 * (D-044, D-045).
 */
export function consumeInput(
  world: World,
  i: number,
  raw: InputBits,
  events: EventSink,
): InputBits {
  const p = world.players[i]!
  updateRide(world, i, events)
  if (p.respawnAtTick !== NO_TICK) {
    p.prevBits = raw
    return 0
  }
  const tick = world.tick
  const stunned = p.stunnedUntil > tick || p.skidUntil > tick
  const bits = stunned || p.placement > 0 ? 0 : raw

  if (pressed(bits, p.prevBits, PET) && canSummon(world, p)) summon(world, i, events)
  if (pressed(bits, p.prevBits, JUMP)) pressJump(world, p)
  updateJumpRelease(world, p, bits)
  tryJump(world, i, events)

  p.prevBits = raw
  return bits
}

/**
 * A JUMP press: buffered for `tryJump`, except underground (no jumping) and on a Drillmole's
 * first grounded press of the ride, which burrows (`burrowUntil`).
 */
function pressJump(world: World, p: PlayerState): void {
  const { tick } = world
  if (isBurrowed(p, tick)) return
  const ride = activeRide(world, p)
  if (ride?.ability === 'burrow' && p.burrowUntil === 0 && p.grounded) {
    p.burrowUntil = tick + ride.burrowTicks
    return
  }
  p.jumpBufferedUntil = tick + world.cfg.jumpBufferTicks
}

/** Letting go of JUMP while still rising cuts `vy` once (short hop, either jump). */
function updateJumpRelease(world: World, p: PlayerState, bits: InputBits): void {
  if (p.jumpHeldTicks === 0) return
  if (p.vy >= 0) {
    p.jumpHeldTicks = 0
  } else if ((bits & JUMP) !== 0) {
    p.jumpHeldTicks++
  } else {
    p.vy = (p.vy * world.cfg.jumpReleaseKeepPct) / 100
    p.jumpHeldTicks = 0
  }
}

/**
 * Fire a buffered jump (D-044). Grounded or within coyote time → the first jump, which leaves
 * one air jump. In the air with `jumpsLeft > 0` → the double jump at its own launch speed.
 * Landing gives the air jump back (`collide.ts`).
 */
function tryJump(world: World, i: number, events: EventSink): void {
  const p = world.players[i]!
  const { cfg, tick } = world
  if (tick > p.jumpBufferedUntil) return
  const fromGround = p.grounded || tick <= p.coyoteUntil
  if (!fromGround && p.jumpsLeft <= 0) return
  // Riding: a Pufferloon has no air jump (it glides); a Thundertoad jumps higher.
  const ride = activeRide(world, p)
  if (!fromGround && ride?.ability === 'glide') return
  const scale = ride ? ride.jumpVelocityPct / 100 : 1
  if (fromGround) {
    p.vy = -cfg.jumpVelocityPxPerTick * scale
    p.jumpsLeft = 1
  } else {
    p.vy = -cfg.doubleJumpVelocityPxPerTick * scale
    p.jumpsLeft--
  }
  p.grounded = false
  p.jumpBufferedUntil = NO_TICK
  p.coyoteUntil = NO_TICK
  p.jumpHeldTicks = 1
  events.push({ type: 'jump', tick, player: i, double: !fromGround })
}
